/* <antarti-spotlight>: the page around the Spotlight film (the opening, the narration, the lights-down, the reveal,
   the stone size picker). The player is <antarti-seq> (seq3d.js); the guide is the comment at the top of
   sections/spotlight-film.liquid. Scoped to its own section, except the header's wordmark (the opening's FLIP target)
   and data-cue / data-mark on <html> (the header's ink, moonlight and waiting mark). */
(() => {
  'use strict';
  const root = document.documentElement;
  const reduce = window.matchMedia('(prefers-reduced-motion: reduce)');
  const clamp = (x) => Math.min(1, Math.max(0, x));
  const flag = (el, name, on) => { if (on !== el.hasAttribute(name)) el.toggleAttribute(name, on); };
  const REVEAL_LEAD = 26;   // frames: the reveal eases in over the film's last ~2 wheel notches of settle

  class AntartiSpotlight extends HTMLElement {
    connectedCallback() {
      const hero = (this.hero = this.querySelector('antarti-seq'));
      if (!hero) return;   // the film is off: the poster and the card are plain markup
      const offs = (this.offs = []);
      const on = (t, type, fn, o) => { t.addEventListener(type, fn, o); offs.push(() => t.removeEventListener(type, fn, o)); };
      const stage = hero.querySelector('.seq__stage');
      const canvas = hero.querySelector('.seq__canvas');
      const camFilm = hero.querySelector('.hero__cam--film');
      const measureEl = hero.querySelector('.hero__measure');
      const acts = hero.querySelector('.hero__acts');
      const header = document.querySelector('.site-header');
      const hdrH = () => header?.offsetHeight || 0;
      const state = { cue: false, revealing: false, p: 0, leave: 0, phase: 'build', frame: 0 };
      const isStatic = () => hero.classList.contains('is-static');
      const filmReady = () => !!(hero.C && hero.meta && typeof hero.frameToCss === 'function');
      const A = () => hero.meta.anchors[hero.cam];
      const PH = () => hero.meta.phases;
      const PM = () => String(A().measure.mm);   // the film's own size, in mm
      let fitting = false, closeFitting = () => {}, fitOpenedAt = 0, setMm = () => {}, initSizes = () => {};

      // ---- The film's geometry: the player's cover fit and meta.anchors, as custom properties on the film ----
      let FW = 0;
      const tf = (el) => {
        const cs = getComputedStyle(el);
        const m = cs.transform && cs.transform !== 'none' ? new DOMMatrix(cs.transform) : new DOMMatrix();
        const [ox, oy] = cs.transformOrigin.split(' ').map(parseFloat);
        return { m, ox, oy };
      };
      const fwd = (t, x, y) => { const q = t.m.transformPoint(new DOMPoint(x - t.ox, y - t.oy)); return { x: q.x + t.ox, y: q.y + t.oy }; };
      // a point of the frame (0..1) on the stage: the cover fit, then the camera's own transform (the fitting's lift)
      const frameToStage = (fx, fy) => {
        const c = hero.frameToCss(fx, fy), tm = tf(camFilm), a = fwd(tm, c.x, c.y);
        return { x: a.x, y: a.y, k: tm.m.a };
      };
      const fitFrame = () => {
        if (!filmReady()) return;
        const o = hero.frameToCss(0, 0), a = A(), P = (x, y) => hero.frameToCss(x, y);
        const set = (k, v) => hero.style.setProperty(k, v.toFixed(1) + 'px');
        FW = o.w;
        set('--fw', FW);
        const pc = P(a.pool.cx, a.pool.cy);
        set('--pool-cx', pc.x); set('--pool-cy', pc.y); set('--pool-rx', a.pool.rx * o.w); set('--pool-ry', a.pool.ry * o.h);
        const b0 = P(a.piece.x0, a.piece.y0), b1 = P(a.piece.x1, a.piece.y1);
        set('--ring-cx', (b0.x + b1.x) / 2); set('--ring-cy', (b0.y + b1.y) / 2); set('--ring-rx', (b1.x - b0.x) / 2); set('--ring-ry', (b1.y - b0.y) / 2);
        set('--piece-b', b1.y);
        const cl = P(a.clasp.x, a.clasp.y);
        set('--clasp-x', cl.x); set('--clasp-y', cl.y);
        const m = a.measure, m1 = P(m.x1, m.y1), m2 = P(m.x2, m.y2);
        set('--stone-x', (m1.x + m2.x) / 2); set('--stone-y', (m1.y + m2.y) / 2);
        set('--pxmm', Math.hypot(m2.x - m1.x, m2.y - m1.y) / m.mm);
        hero.style.setProperty('--pm', String(m.mm));
        placeMeasure();
      };
      // the measure on the stone of the size on screen (meta.sizes[d].anchors[cam].measure), and its offset from the
      // film's own stone (--ms-dx / --ms-dy) for the ruler's link line; hidden while the ring turns (data-seq-swap)
      const placeMeasure = () => {
        if (!measureEl || !filmReady()) return;
        const a = A().measure, m = hero.meta.sizes?.[hero.dataset.seqSize]?.anchors?.[hero.cam]?.measure || a;
        const P = (x, y) => hero.frameToCss(x, y), m1 = P(m.x1, m.y1), m2 = P(m.x2, m.y2), a1 = P(a.x1, a.y1), a2 = P(a.x2, a.y2);
        const pxmm = Math.hypot(a2.x - a1.x, a2.y - a1.y) / a.mm;
        const top = (m1.y + m2.y) / 2 + pxmm * m.mm * 0.67, top0 = (a1.y + a2.y) / 2 + pxmm * a.mm * 0.67;
        measureEl.style.left = ((m1.x + m2.x) / 2).toFixed(1) + 'px';
        measureEl.style.top = top.toFixed(1) + 'px';
        for (const [k, v] of [['--pm', m.mm], ['--pm2', m.mm / 2], ['--mm', m.mm]]) measureEl.style.setProperty(k, String(v));
        hero.style.setProperty('--ms-dx', (m1.x - a1.x).toFixed(1) + 'px');
        hero.style.setProperty('--ms-dy', (top - top0).toFixed(1) + 'px');
        const label = measureEl.querySelector('.measure__mm');
        if (label) label.textContent = String(m.mm);
        fitPush();
      };
      // desktop, in the fitting: the ruler's overlap with the store row on a short screen (--fit-push; CSS takes it
      // from the reveal's bottom margin, then lifts the camera). Layout offsets only, so it never feeds back.
      const ruler = this.querySelector('.ruler'), row = this.querySelector('.fitting__row');
      const fitPush = () => {
        if (!fitting || !ruler || !row || ruler.hidden || !matchMedia('(min-width: 768px)').matches) {
          if (hero.style.getPropertyValue('--fit-push')) hero.style.removeProperty('--fit-push');
          return;
        }
        const top = (el) => { let y = 0; for (let n = el; n && n !== stage; n = n.offsetParent) y += n.offsetTop; return y; };
        const p = parseFloat(hero.style.getPropertyValue('--fit-push')) || 0, lift = -48 - Math.max(0, p - 32);
        const over = top(ruler) + ruler.offsetHeight + 16 + 8 + lift - top(row), q = Math.max(0, Math.round(p + over));
        if (q !== p) hero.style.setProperty('--fit-push', q + 'px');
      };

      // ---- The opening: the big mark contracts into the header (a FLIP) and its i-stone falls into the film ----
      const mark = hero.querySelector('.hero__mark');
      const wmBig = mark?.querySelector('.hero__wm');
      const wmIn = mark?.querySelector('.wm');
      const dotPh = mark?.querySelector('.tittle__dot');
      const drop = hero.querySelector('.hero__drop');
      const hdrWm = header?.querySelector('.wordmark .wm--stone');
      const easeFlip = getComputedStyle(root).getPropertyValue('--sl-ease-flip').trim() || 'ease-in-out';
      const easeBack = getComputedStyle(root).getPropertyValue('--sl-ease-wipe').trim() || 'ease-out';
      let M = null, lastBuild = 0, markState = 'init';
      const placeDrop = (x, y, s, o) => {
        drop.style.transform = `translate3d(${(x - M.box / 2).toFixed(2)}px, ${(y - M.box / 2).toFixed(2)}px, 0) scale(${(s / M.size).toFixed(4)})`;
        drop.style.opacity = o.toFixed(3);
      };
      // a blend from the i's dot to the film's own i-stone (meta.hero[cam] at the painted position): it leaves the i at
      // the cue on a falling arc, sits on the film's stone from handoffStart and fades out by handoffEnd
      const paintDrop = () => {
        if (!M || !drop || !filmReady() || !hero.meta.hero) return;
        const f = Math.max(0, hero.pos ?? hero.drawn ?? 0), ph = PH(), track = hero.meta.hero[hero.cam];
        let hs = ph.handoffStart;
        while (hs < ph.handoffEnd && track[hs] && track[hs].v === 0) hs++;
        if (hs !== ph.handoffStart && !paintDrop.warned) { paintDrop.warned = true; console.warn(`meta.hero: v = 0 at handoffStart; the i-stone aims at frame ${hs}`); }
        const he = Math.max(hs + 1, ph.handoffEnd);
        const f0 = Math.min(hs - 0.5, hero.frameOf(0.02 * hero.pSpiral));   // the frame at the cue (build .02)
        const ft = Math.min(track.length - 1, Math.max(f, hs)), i0 = Math.floor(ft), u = ft - i0;
        const T0 = track[i0], T1 = track[Math.min(track.length - 1, i0 + 1)];
        const T = { x: T0.x + (T1.x - T0.x) * u, y: T0.y + (T1.y - T0.y) * u, d: T0.d + (T1.d - T0.d) * u };
        const q = frameToStage(T.x, T.y), s = T.d * FW * q.k;
        const w = f >= hs ? 1 : clamp((f - f0) / (hs - f0));
        const ex = 1 - (1 - w) * (1 - w), ey = w * w;   // out sideways first, then falling
        const o = f < hs ? 1 : f >= he ? 0 : 1 - (f - hs) / (he - hs);
        placeDrop(M.x0 + (q.x - M.x0) * ex, M.y0 + (q.y - M.y0) * ey, M.size + (s - M.size) * w, o);
      };
      let stoneSet = false;
      const setStone = () => {
        if (stoneSet || !drop || !hero.stoneUrl) return;
        stoneSet = true;
        const probe = new Image();
        probe.onload = () => drop.style.setProperty('--hero-stone', `url("${probe.src}")`);
        probe.src = hero.stoneUrl;
      };
      const setMark = (v) => { if (root.dataset.mark !== v) root.dataset.mark = v; };
      const cue = (go) => {
        const k = `translate(${M.dx.toFixed(2)}px, ${M.dy.toFixed(2)}px) scale(${M.k.toFixed(4)})`;
        this.markAnim?.cancel(); clearTimeout(this.markTimer);
        if (go) {
          markState = 'landing'; setMark('landing');
          this.markAnim = wmBig.animate([{ transform: 'none' }, { transform: k }], { duration: 650, easing: easeFlip, fill: 'forwards' });
          // the header's own mark comes on underneath 50ms before the landing (the two share their pixels by then)
          this.markTimer = setTimeout(() => { if (markState === 'landing') setMark('settling'); }, 600);
          this.markAnim.onfinish = () => { if (markState === 'landing') { markState = 'landed'; setMark('landed'); } };
        } else {
          markState = 'open'; setMark('open');
          this.markAnim = wmBig.animate([{ transform: k }, { transform: 'none' }], { duration: 250, easing: easeBack, fill: 'forwards' });
        }
        schedule();
      };
      const paintMark = (build) => {
        lastBuild = build;
        if (!M) return;
        const lit = build > 0.02;
        if (markState === 'init') { markState = lit ? 'landed' : 'open'; setMark(markState); return; }   // arriving mid-page
        if (lit && markState === 'open') cue(true);
        else if (!lit && (markState === 'landed' || markState === 'landing')) cue(false);
      };
      const measureMark = () => {
        const h = hdrWm?.getBoundingClientRect();
        if (!mark || !h?.width || reduce.matches || isStatic()) {
          M = null; markState = 'init'; delete root.dataset.mark;
          flag(this, 'data-nomark', !!mark && !isStatic() && !reduce.matches);
          return;
        }
        flag(this, 'data-nomark', false);
        this.markAnim?.cancel();
        const s = stage.getBoundingClientRect(), o = wmBig.getBoundingClientRect(), a = wmIn.getBoundingClientRect();
        const d = dotPh.getBoundingClientRect();
        const size = Math.max(d.width, 10);
        M = { dx: h.left - a.left, dy: h.top - a.top, k: h.width / a.width, x0: d.left - s.left + d.width / 2, y0: d.top - s.top + d.height / 2, size, box: size / 0.85 };
        wmBig.style.transformOrigin = `${(a.left - o.left).toFixed(1)}px ${(a.top - o.top).toFixed(1)}px`;
        drop.style.setProperty('--ds', M.box.toFixed(1) + 'px');
        if (markState === 'landed') this.markAnim = wmBig.animate([{ transform: `translate(${M.dx}px, ${M.dy}px) scale(${M.k})` }], { duration: 0, fill: 'forwards' });
        paintMark(lastBuild);
        paintDrop();
      };

      // ---- Where the stage is: the lockup's exit, the piece leaving, scrolling out closes the fitting ----
      const measureStage = () => {
        const vh = window.innerHeight, r = hero.getBoundingClientRect(), beyond = vh - r.bottom;
        state.leave = clamp(beyond / (0.12 * vh));
        flag(this, 'data-leaving', beyond - 0.12 * vh > 0.0028 * vh);   // exit > .01 of the next 28svh
        flag(this, 'data-lockup-out', beyond > 1);
        if (fitting && state.leave > 0) scrollClose();
        if (measureEl) flag(this, 'data-piece-gone', measureEl.getBoundingClientRect().top < hdrH() + 8);
      };

      // ---- The lights go down in the film; the page follows the frame it has drawn ----
      const setCue = (v) => {
        if (v === state.cue) return;
        state.cue = v;
        flag(hero, 'data-cue', v); flag(root, 'data-cue', v);
        schedule();
      };
      const setReveal = (v) => {
        if (v === state.revealing) return;
        state.revealing = v;
        flag(this, 'data-reveal', v);
        schedule();
      };
      const lights = (d) => {
        if (!filmReady()) return;
        const f = hero.drawn, ph = PH();
        setCue((d?.cue ?? (f >= ph.lightsDown ? 1 : 0)) >= 0.5);
        const tgt = typeof hero.target === 'number' ? hero.target : f, at = ph.rest - REVEAL_LEAD;
        setReveal(state.revealing ? tgt >= at - 4 : tgt >= at && f >= ph.rest - 2 * REVEAL_LEAD);
      };
      // the narration, from the drawn frame: loose stones, set in silver, moissanite, worn every day; none at rest
      const actFor = (f, phase) => {
        if (phase === 'rest' || !filmReady()) return 0;
        const ph = PH();
        if (f < ph.handoffStart) return 0;
        if (f < ph.handoffEnd) return 1;
        if (f < ph.spiralEnd / 2) return 2;
        if (f < (ph.heroSet ?? ph.spiralEnd)) return 3;
        return 4;
      };
      // The header over the studio: ink while the lights are up (the mock-up), moonlight once they are down. Where ink
      // does not reach 4.5:1 on the film's own top colour at the drawn frame (meta.colours: what is behind the header),
      // one soft veil of that colour taken paler to 5.5:1 runs across the bar (--sl-hdr-halo, section-header.css).
      // Nothing is sampled.
      const rgbOf = (h) => { const n = parseInt(String(h).trim().slice(1), 16); return [n >> 16, (n >> 8) & 255, n & 255]; };
      const lin = (v) => { v /= 255; return v <= 0.03928 ? v / 12.92 : ((v + 0.055) / 1.055) ** 2.4; };
      const lum = (c) => 0.2126 * lin(c[0]) + 0.7152 * lin(c[1]) + 0.0722 * lin(c[2]);
      const ratio = (a, b) => (Math.max(a, b) + 0.05) / (Math.min(a, b) + 0.05);
      const cs = getComputedStyle(root);
      const INK = rgbOf(cs.getPropertyValue('--sl-film-ink'));   // token (base.css)
      const toward = (c, d, t) => c.map((v, i) => Math.round(v + (d[i] - v) * t));
      const paintHeader = (top) => {
        const c = !isStatic() && !state.cue && top ? rgbOf(top) : null, L = c && lum(c);
        flag(root, 'data-film-ink', !!c);
        const halo = !!c && ratio(lum(INK), L) < 4.5;
        if (halo) {
          let t = 0;
          while (t < 1 && ratio(lum(INK), lum(toward(c, [255, 255, 255], t))) < 5.5) t += 0.05;
          root.style.setProperty('--sl-hdr-halo', `rgb(${toward(c, [255, 255, 255], t).join(' ')})`);
        }
        flag(root, 'data-film-halo', halo);
      };
      // scrolling out of the hold closes the fitting, never a scroll the fitting started itself (400ms grace)
      const scrollClose = () => { if (performance.now() - fitOpenedAt > 400) closeFitting(false); };

      on(hero, 'antarti-seq:progress', (e) => {
        const d = e.detail || {};
        state.p = d.p || 0; state.frame = d.frame || 0; state.phase = d.phase || 'build';
        if (hero.drawn >= 0) flag(this, 'data-painted', true);
        if (isStatic()) { setCue(true); setReveal(true); } else lights(d);
        paintHeader(d.colours?.top);
        paintDrop();   // in the same frame the film drew
        if (fitting && !isStatic() && (d.phase !== 'rest' || hero.target < hero.last - 0.5)) scrollClose();
        paintMark(d.build || 0);
        flag(hero, 'data-left', (d.build || 0) > 0.02);
        const n = String(isStatic() ? 0 : actFor(state.frame, d.phase));
        if (hero.dataset.act !== n) {
          hero.dataset.act = n;
          if (acts && n !== '0') acts.style.setProperty('--sheet-h', acts.offsetHeight + 'px');
        }
        schedule();
      });
      on(hero, 'antarti-seq:failed', () => { setReveal(true); setCue(true); });
      if (hero.failed || hero.classList.contains('is-failed')) { setReveal(true); setCue(true); }   // failed before this ran

      // ---- One frame loop, only while something moves ----
      const frame = () => { this.raf = 0; measureStage(); fitFrame(); paintDrop(); };
      const schedule = () => { if (!this.raf) this.raf = requestAnimationFrame(frame); };
      on(window, 'scroll', schedule, { passive: true });

      // ---- Phones: the guarantees stand on the stage's own dark floor, under the reveal's actions ----
      const floor = this.querySelector('.sl-film__floor');
      const measureTrust = () => { if (floor?.parentElement === stage) stage.style.setProperty('--trust-h', floor.offsetHeight + 'px'); };
      if (floor && matchMedia('(max-width: 767px)').matches) { stage.appendChild(floor); measureTrust(); }

      on(window, 'resize', () => { fitFrame(); measureMark(); measureTrust(); schedule(); }, { passive: true });
      on(hero, 'antarti-seq:camera', () => { setStone(); fitFrame(); measureMark(); initSizes(); if (!fitting) setMm(); schedule(); });
      document.fonts?.ready.then(() => { if (this.offs === offs) { measureMark(); measureTrust(); schedule(); } });
      if ('ResizeObserver' in window && canvas) {
        this.ro = new ResizeObserver(() => { fitFrame(); schedule(); });
        this.ro.observe(canvas);
      }
      measureMark();
      schedule();

      // ---- The card is reachable by keyboard at any point: focusing it plays the film to the hold ----
      const reveal = hero.querySelector('.hero__reveal');
      // the hold: halfway from the rest frame to the end of the film's scroll (never before the rest frame)
      const holdP = () => (filmReady() ? Math.max(hero.pRest, Math.min(0.97, (hero.pRest + 1) / 2 + 0.01)) : 0.92);
      const holdY = () => window.scrollY + hero.getBoundingClientRect().top + (hero.offsetHeight - window.innerHeight) * holdP();
      on(reveal, 'focusin', () => {
        if (state.revealing || isStatic() || fitting) return;
        window.scrollTo({ top: holdY(), behavior: 'auto' });
      });
      // the editor: selecting the section shows the card (the hold, or the still)
      on(document, 'shopify:section:select', (e) => {
        if (e.detail?.sectionId !== this.dataset.sectionId) return;
        if (isStatic()) this.scrollIntoView({ block: 'start' });
        else window.scrollTo({ top: holdY(), behavior: 'auto' });
      });

      // ---- The fitting: "Choose a stone size" opens the sizes in their own place, on the stage ----
      const fitWrap = this.querySelector('.fitting');
      const sizeBtn = this.querySelector('.reveal__size');
      const lockMm = hero.querySelector('.hero__mm');
      const lockNm = hero.querySelector('.hero__nm');
      let sizes = {};
      try { sizes = JSON.parse(this.querySelector('script[data-sl-sizes]')?.textContent || '{}'); } catch { /* no sizes */ }
      const setLock = (mm) => {
        if (lockMm) lockMm.textContent = `${mm}mm`;
        if (lockNm && sizes[mm]?.name) lockNm.textContent = sizes[mm].name;
      };
      on(hero, 'antarti-seq:size', (e) => {
        if (e.detail?.shown) setLock(e.detail.shown);
        if (e.detail?.size) placeMeasure();
      });
      if (!fitWrap || !sizeBtn || !ruler) {
        setMm = () => { if (filmReady()) setLock(PM()); };
        return;
      }
      const store = fitWrap.querySelector('.fitting__store');
      const shop = hero.querySelector('.reveal__shop');
      const fWish = fitWrap.querySelector('.fitting__wish');
      const fWishT = fWish?.querySelector('.fitting__wish-t');
      let radios = [...ruler.querySelectorAll('input[type="radio"]')];
      const inFit = (el) => !!el && (fitWrap.contains(el) || ruler.contains(el));
      let handle = '', ready = false;
      const wishlist = () => window.antarti?.wishlist;
      const syncWish = () => {
        if (!fWish || !handle) return;
        const saved = !!wishlist()?.has(handle);
        fWish.setAttribute('aria-pressed', String(saved));
        fWishT.textContent = saved ? fWish.dataset.saved : fWish.dataset.save;
      };
      on(document, 'antarti:wishlist', syncWish);
      // a stop only for a size the film has a clip for and a product exists for; fewer than two (or no stop for the
      // film's own size): no picker
      initSizes = () => {
        if (ready || !filmReady()) return;
        ready = true;
        radios = radios.filter((r) => {
          const keep = !!hero.meta.sizes?.[r.value] && !!sizes[r.value];
          if (!keep) r.closest('.stop').remove();
          return keep;
        });
        sizeBtn.hidden = radios.length < 2 || !radios.some((r) => r.value === PM());
      };
      // the link, its label and Save follow the tap; the lockup follows the ring on screen (antarti-seq:size)
      setMm = (mm = filmReady() ? PM() : '') => {
        const p = sizes[mm];
        if (!p) { if (mm) setLock(mm); return; }
        handle = p.handle;
        store.href = p.url;
        store.textContent = store.dataset.label.replace('{size}', `${mm}mm`);
        if (shop) shop.href = p.url;
        hero.dataset.mm = mm;
        radios.forEach((r) => { r.checked = r.value === mm; });
        hero.playSize?.(null, mm);
        if (!hero.sz) setLock(mm);   // no ring turns (no size clips): now
        syncWish();
      };
      radios.forEach((r) => on(r, 'change', () => { if (r.checked) setMm(r.value); }));
      if (fWish) on(fWish, 'click', () => { if (handle) wishlist()?.toggle(handle); syncWish(); });
      // pressed outside the hold (the stage leaving, or parked at its very end): glide back to the hold first
      const inHold = () => isStatic() || (state.phase === 'rest' && state.p < 0.975 && state.leave === 0);
      const doOpen = () => {
        if (fitting || !this.isConnected) return;
        fitting = true;
        fitOpenedAt = performance.now();
        fitWrap.hidden = false; ruler.hidden = false;
        sizeBtn.setAttribute('aria-expanded', 'true');
        flag(this, 'data-fitting', true);
        hero.sizesOpen?.(true);
        fitPush();
        (radios.find((r) => r.checked) || radios.find((r) => r.value === PM()) || radios[0])?.focus({ preventScroll: true });
        schedule();
      };
      const openFit = () => {
        if (fitting) return;
        if (inHold()) return doOpen();
        window.scrollTo({ top: holdY(), behavior: reduce.matches ? 'auto' : 'smooth' });
        let last = -1, same = 0;
        const t0 = performance.now();
        const settle = () => {
          const y = window.scrollY;
          same = Math.abs(y - last) < 0.5 ? same + 1 : 0; last = y;
          if ((same >= 4 && state.cue) || performance.now() - t0 > 1600) doOpen(); else this.settleRaf = requestAnimationFrame(settle);
        };
        this.settleRaf = requestAnimationFrame(settle);
      };
      closeFitting = (refocus) => {
        if (!fitting) return;
        const had = inFit(document.activeElement);
        fitting = false;
        flag(this, 'data-fitting', false);
        fitWrap.hidden = true; ruler.hidden = true;
        sizeBtn.setAttribute('aria-expanded', 'false');
        setMm(PM());   // the ring plays back to the film's own size
        hero.sizesOpen?.(false);
        if (refocus || had) sizeBtn.focus({ preventScroll: true });
        schedule();
      };
      on(sizeBtn, 'click', openFit);
      on(fitWrap.querySelector('.fitting__close'), 'click', () => closeFitting(true));
      on(document, 'keydown', (e) => { if (e.key === 'Escape' && fitting) closeFitting(true); });
      // tabbing out closes it; a click on the stage moves focus to a focusable ancestor (#MainContent has tabindex -1)
      // first, which is not leaving
      [fitWrap, ruler].forEach((box) => on(box, 'focusout', (e) => {
        const to = e.relatedTarget;
        if (to && !inFit(to) && to !== sizeBtn && !to.contains(box)) closeFitting(false);
      }));
      if (filmReady()) { initSizes(); setMm(); }
    }

    disconnectedCallback() {
      this.offs?.forEach((off) => off());
      this.offs = null;
      cancelAnimationFrame(this.raf); this.raf = 0;
      cancelAnimationFrame(this.settleRaf);
      clearTimeout(this.markTimer);
      this.markAnim?.cancel();
      this.ro?.disconnect();
      for (const a of ['data-cue', 'data-mark', 'data-film-ink', 'data-film-halo']) root.removeAttribute(a);
    }
  }

  (window.antarti?.define || ((n, c) => customElements.get(n) || customElements.define(n, c)))('antarti-spotlight', AntartiSpotlight);
})();
